import React, { useState, useEffect }  from 'react';
import QuoteBuilderForm from 'components/quote_builder/QuoteBuilderForm.jsx';
import SettingsUserUnitPriceAttributeList from 'components/quote_builder/SettingsUserUnitPriceAttributeList.jsx';
import { GetRequest } from 'components/common';
import { sectionGroupFor } from 'components/quote_builder/settingsSiblingSections';

const SettingsTabs = ( { config, breadcrumbs, showingState, afterSave, afterCancel, editStateId, hideDeleteFromList, editId, editAttributeId, showNewForm, activeTab, onTabChange, showNewAttributeForm, setActiveItem } ) => {
  const unit = window.user_unit || 'mm'

  // Item counts shown in the tab labels. Same source as the settings menu:
  // unit-price counts keyed by section key, machine counts keyed by machine-name key.
  const [counts, setCounts] = useState({});
  const [attributeCounts, setAttributeCounts] = useState({});
  const fetchCounts = () => {
    GetRequest({
      url: '/quote_builder_user_unit_price_counts',
      callback: (data) => {
        setCounts({ ...data.counts });
        setAttributeCounts({ ...data.attribute_counts });
      }
    });
  };
  useEffect(() => {
    document.addEventListener('quote_builder.user_unit_price_deleted', fetchCounts);
    document.addEventListener('quote_builder.user_unit_price_saved', fetchCounts);
    return () => {
      document.removeEventListener('quote_builder.user_unit_price_deleted', fetchCounts);
      document.removeEventListener('quote_builder.user_unit_price_saved', fetchCounts);
    };
  }, []);
  useEffect(fetchCounts, []);

  const suffix = (count) => {
    if (count == undefined || count == 0) { return ''; }
    return ` (${count})`;
  };

  // Count for an attribute tab. UserUnitPriceAttributesByUnit tabs split their
  // attributes by unit (mm/in), so use the one matching the current unit.
  const attributeTabCount = (tabConfig) => {
    if (tabConfig.form_type == 'UserUnitPriceAttributesByUnit') {
      const sub = (tabConfig.attributes || []).find(a => a.unit == unit);
      return sub ? attributeCounts[sub.key] : undefined;
    }
    return attributeCounts[tabConfig.key];
  };

  // Use editId from props if provided (from URL), otherwise use editStateId
  const effectiveEditStateId = editId || editStateId;
  // Determine showing state: 'new' if showNewForm, 'edit' if editId, otherwise use passed showingState
  const effectiveShowingState = showNewForm ? 'new' : (editId ? 'edit' : showingState);

  const tabsConfig = config.tabs;
  if(tabsConfig == undefined) { return <QuoteBuilderForm
    config={config}
    showingState={effectiveShowingState}
    afterSave={afterSave}
    afterCancel={afterCancel}
    editStateId={effectiveEditStateId}
    hideDeleteFromList={hideDeleteFromList}
    showNewForm={showNewForm}
  />}

  const initialTabIndex = () => {
    if (!activeTab || !tabsConfig) { return 0; }
    const index = tabsConfig.findIndex(tab => tab.key === activeTab);
    return index >= 0 ? index : 0;
  };

  const [tabIndex, setTabIndex] = useState(initialTabIndex)
  const _setTabIndex = (e, i) => {
    e.preventDefault();
    setTabIndex(() => i)
    if (onTabChange) {
      const tabKey = (tabsConfig[i] && tabsConfig[i].key) || null;
      onTabChange(tabKey);
    }
  }

  // Sync tab index when activeTab changes (e.g. browser back/forward)
  useEffect(() => {
    if (!tabsConfig) { return; }
    if (activeTab) {
      const index = tabsConfig.findIndex(tab => tab.key === activeTab);
      setTabIndex(index >= 0 ? index : 0);
    } else {
      setTabIndex(0);
    }
  }, [activeTab])

  const getTab = (tab, i) => {
    return <li role="presentation" class={tabIndex == i ? 'active' : ''}> <a href="#" onClick={ (e) => _setTabIndex(e, i) }> { tabTitle( tabsConfig[i] ) } </a> </li>
  }


  const isNewFormTab = (tabConfig) => {
    return showNewAttributeForm && tabConfig.key === activeTab;
  };

  // The deep-linked attribute id to open in edit mode, but only for the tab the URL points at
  // (its key is the active tab). Mirrors isNewFormTab so edit and new deep-links behave alike.
  const editAttributeIdForTab = (tabConfig) => {
    return (editAttributeId && tabConfig.key === activeTab) ? editAttributeId : null;
  };

  const UserUnitPriceAttributesByUnitContent = () => {
    const tabConfig = tabsConfig[ tabIndex ];

    return <>
      {tabConfig.attributes.map( (attributeConfig) => {
        return unit == attributeConfig.unit && <SettingsUserUnitPriceAttributeList userUnitPriceKey={config.key} tabConfig={attributeConfig} urlTabKey={tabConfig.key} sectionAttributes={config.attributes} showNewAttributeForm={isNewFormTab(tabConfig)} editAttributeId={editAttributeIdForTab(tabConfig)} />
      } ) }
    </>
  }

  const tabContent = () => {
    const tabConfig = tabsConfig[ tabIndex ];

    switch(tabConfig.form_type) {
    case "UserUnitPrices":
      return <QuoteBuilderForm
        config={config}
        showingState={effectiveShowingState}
        afterSave={afterSave}
        afterCancel={afterCancel}
        editStateId={effectiveEditStateId}
        hideDeleteFromList={hideDeleteFromList}
        showNewForm={showNewForm}
        />
      break;

    case "UserUnitPriceAttribute":
      return <SettingsUserUnitPriceAttributeList userUnitPriceKey={config.key} tabConfig={tabConfig} sectionAttributes={config.attributes} showNewAttributeForm={isNewFormTab(tabConfig)} editAttributeId={editAttributeIdForTab(tabConfig)} />
      break;

     case "UserUnitPriceAttributesByUnit":
      return UserUnitPriceAttributesByUnitContent()
      break;

    default:
      throw "[error] tabContent cannot be found for " + tabConfig.form_type;
    }

    return <>

    </>
  }

  const tabTitle = (tabConfig) => {
    switch(tabConfig.form_type) {
    case "UserUnitPrices":
      return t("quote_builder.my_unit_prices") + suffix(counts[config.key]);
      break;

    case "UserUnitPriceAttribute":
      return t(tabConfig.label) + suffix(attributeTabCount(tabConfig))
      break;

    case "UserUnitPriceAttributesByUnit":
      return t(tabConfig.label) + suffix(attributeTabCount(tabConfig))
      break;

    default:
      throw "[error] tabTitle cannot be found for " + tabConfig.form_type;
    }
  }

  // Whether a group entry is the one currently shown. Plain section entries match
  // on key alone. Tab-scoped entries (printing, flexo printing & cutting — one
  // section split into a unit-prices line and a machines line) match the sidebar's
  // null-vs-non-null tab rule: the null entry is current for the unit-prices tab,
  // the machines entry for any other tab.
  const isCurrentSection = (section) => {
    if (section.key != config.key) { return false; }
    if (section.tab === undefined) { return true; }
    return section.tab ? !!activeTab : !activeTab;
  }

  // Subtle links to the entries in the current section's group, floated to the
  // top-right of the header and kept in the same order as the left menu. Clicking
  // one switches to that section (and tab) via setActiveItem. The current entry is
  // rendered as a disabled (muted, non-clickable) item so the row of links stays
  // stable when switching between sections.
  const siblingLinks = () => {
    if (setActiveItem == undefined) { return null; }
    const group = sectionGroupFor(config.key);
    if (group.length == 0) { return null; }
    return (
      <span className="settings-sibling-links" style={{ float: "right", fontSize: "13px", fontWeight: "normal", marginTop: "6px" }}>
        {group.map( (section, index) => (
          <React.Fragment key={index}>
            {index > 0 && <span style={{ color: "#ccc", margin: "0 6px" }}>·</span>}
            {isCurrentSection(section)
              ? <span style={{ color: "#999" }}>{t(section.label)}</span>
              : <a href="#" style={{ color: "#eb5424" }} onClick={ (e) => { e.preventDefault(); setActiveItem(section.key, section.tab); } }>{t(section.label)}</a>
            }
          </React.Fragment>
        ) )}
      </span>
    );
  }

  const breadcrumbHeader = () => {
    if(breadcrumbs == undefined) { return }
    return <><div className="page-in-name"> {siblingLinks()} {breadcrumbs.split(",").map( (br) => t("quote_builder." + br)  ).join(" | ") } </div> <hr /></>
  }
  return (
    <div>
      {breadcrumbHeader()}
      <ul class="nav nav-pills">
        { config.tabs != undefined && config.tabs.map(getTab) }
      </ul>

      <div key={tabIndex} style={{marginTop: "10px"}}> {tabContent()} </div>
    </div>
  )
}

export default SettingsTabs;
